"use client";

import { useEffect, useRef, useState } from "react";

type UseHashPaginationOptions = {
  /**
   * Whether this hook should be active
   * @default true
   */
  enabled?: boolean;
};

/**
 * Custom hook for handling hash-based pagination with tab switching
 * Supports hash formats like: #tabId or #tabId&page=2
 *
 * @param tabPatterns - Array of tab patterns to match against (without the #)
 * @param onMatch - Callback function that executes when hash matches. Receives { tab, page }
 * @param options - Optional configuration options
 * @returns The currently matched hash info { tab, page, fullHash } or null if no match
 */
export function useHashPagination(
  tabPatterns: string[],
  onMatch: (data: { tab: string; page: number; fullHash: string }) => void,
  options: UseHashPaginationOptions = {},
) {
  const { enabled = true } = options;
  const [matchedData, setMatchedData] = useState<{
    tab: string;
    page: number;
    fullHash: string;
  } | null>(null);

  const onMatchRef = useRef(onMatch);
  const tabPatternsRef = useRef(tabPatterns);

  useEffect(() => {
    onMatchRef.current = onMatch;
  }, [onMatch]);

  useEffect(() => {
    tabPatternsRef.current = tabPatterns;
  }, [tabPatterns]);

  useEffect(() => {
    const checkAndExecute = (): boolean => {
      if (!enabled || tabPatternsRef.current.length === 0) {
        setMatchedData(null);
        return false;
      }

      const hash = window.location.hash.slice(1);
      if (!hash) {
        setMatchedData(null);
        return false;
      }

      const [tabPart, pagePart] = hash.split("&");
      const pageMatch = pagePart?.match(/page=(\d+)/);
      const pageNumber = pageMatch ? parseInt(pageMatch[1], 10) : 1;

      const matchedPattern = tabPatternsRef.current.find((pattern) => pattern === tabPart);
      if (matchedPattern) {
        const data = {
          tab: matchedPattern,
          page: pageNumber,
          fullHash: hash,
        };
        setMatchedData(data);
        onMatchRef.current(data);
        return true;
      }

      setMatchedData(null);
      return false;
    };

    checkAndExecute();

    const handleHashChange = () => {
      checkAndExecute();
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [enabled]);

  return matchedData;
}
